/**
 * Vault descriptor pinning (VD — H-12, H-17, ENTX-A-3).
 *
 * SSP Wallet and SSP Key no longer trust the relay for an enterprise vault's
 * script or address. Every vault request the app sends to the devices carries
 * the vault's full signer set (the "descriptor") and its hash. The devices
 * verify it cryptographically, pin it on first use, and re-derive every script,
 * change output, EVM sender and deposit address from the pinned set.
 *
 * The app is only the courier:
 *  - it fetches `{ descriptor, descriptorHash }` from the relay right before a
 *    device request,
 *  - checks the response is well-formed and belongs to the vault and request
 *    it is about to send (cheap integrity checks, never a substitute for the
 *    device's own verification),
 *  - and forwards both values UNCHANGED (same object, no re-serialization) as
 *    `vaultDescriptor` + `vaultDescriptorHash` in the window.ssp params.
 *
 * If the descriptor can't be fetched or fails those checks, the wallet is
 * never called (fail closed).
 *
 * Only for a wallet that advertises `vault_descriptor_v1` (walletCapabilities).
 * Released wallets (2.1.3 and older) refuse a request carrying the descriptor
 * object outright and never verified one, so they get today's params. That is
 * no downgrade for new wallets: they refuse a vault request without it.
 */

import { WALLET_CAPABILITY, walletHasCapability } from './walletCapabilities';

export const VAULT_DESCRIPTOR_VERSION = 1;

/** One signer of the vault, as the relay returns it (VD-1). */
export interface VaultDescriptorMember {
  wkIdentity: string;
  walletPubKey: string;
  keyPubKey: string;
  /** Vault xpubs as submitted; null when the signing mode doesn't use it. */
  xpubWallet?: string | null;
  xpubKey?: string | null;
  /** xpub attestations as stored at submitXpub; null when absent. */
  walletXpubSignature?: string | null;
  keyXpubSignature?: string | null;
}

/** Descriptor v1 (VD-1). Members are sorted by wkIdentity ascending. */
export interface VaultDescriptor {
  v: 1;
  orgId: string;
  vaultId: string;
  orgIndex: number;
  vaultIndex: number;
  chain: string;
  signingMode: string | null;
  requiredSigners: number;
  members: VaultDescriptorMember[];
}

export interface VaultDescriptorBundle {
  descriptor: VaultDescriptor;
  /** sha256 hex of canonicalJson(descriptor), computed by the relay. */
  descriptorHash: string;
}

/** The two params every VD-aware window.ssp request carries (VD-2). */
export interface VaultDescriptorParams {
  vaultDescriptor: VaultDescriptor;
  vaultDescriptorHash: string;
}

export type VaultDescriptorErrorCode =
  /** The descriptor could not be fetched (network, auth, server error). */
  | 'VAULT_DESCRIPTOR_UNAVAILABLE'
  /** 409: not every signer has submitted their keys yet. */
  | 'VAULT_DESCRIPTOR_NOT_READY'
  /** The relay's response is not a well-formed descriptor v1. */
  | 'VAULT_DESCRIPTOR_BAD_RESPONSE'
  /** The descriptor describes another vault/org/chain than the request. */
  | 'VAULT_DESCRIPTOR_REQUEST_MISMATCH';

/** App-side descriptor failure. Nothing has been sent to the wallet. */
export class VaultDescriptorError extends Error {
  errorCode: VaultDescriptorErrorCode;

  constructor(errorCode: VaultDescriptorErrorCode, message?: string) {
    super(message ?? errorCode);
    this.name = 'VaultDescriptorError';
    this.errorCode = errorCode;
  }
}

const ORG_INDEX_MIN = 100;
const ORG_INDEX_MAX = 99999;
const VAULT_INDEX_LIMIT = 2 ** 31;
const HASH_RE = /^[0-9a-f]{64}$/;
const COMPRESSED_PUBKEY_RE = /^0[23][0-9a-fA-F]{64}$/;

/** Server error codes that mean "vault not fully set up" (the endpoint's 409). */
const NOT_READY_CODES = new Set([
  'VAULT_NOT_ACTIVE',
  'VAULT_NOT_READY',
  'VAULT_DESCRIPTOR_NOT_READY',
  'DESCRIPTOR_NOT_AVAILABLE',
  'DESCRIPTOR_NOT_READY',
  'XPUBS_PENDING',
]);

/**
 * Server refusals meaning the stored signer set can't form a valid descriptor
 * (shown like a malformed response). Access/lookup refusals (NOT_MEMBER,
 * VAULT_ACCESS_DENIED, NOT_FOUND, INVALID_ID) stay "unavailable". Both fail closed.
 */
const SIGNER_SET_CODES = new Set([
  'MISSING_PUBKEYS',
  'MISSING_XPUB',
  'INVALID_SIGNING_MODE',
  'INVALID_THRESHOLD',
  'SIGNER_SET_MISMATCH',
  'DUPLICATE_SIGNER',
]);

/** The app error code for a `{ success:false, errorCode }` descriptor answer. */
export function descriptorRefusalCode(
  status: number | undefined,
  errorCode: unknown
): VaultDescriptorErrorCode {
  if (isDescriptorNotReady(status, errorCode)) return 'VAULT_DESCRIPTOR_NOT_READY';
  if (typeof errorCode === 'string' && SIGNER_SET_CODES.has(errorCode)) {
    return 'VAULT_DESCRIPTOR_BAD_RESPONSE';
  }
  return 'VAULT_DESCRIPTOR_UNAVAILABLE';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isIntegerInRange(value: unknown, min: number, maxExclusive: number): value is number {
  return (
    typeof value === 'number' && Number.isInteger(value) && value >= min && value < maxExclusive
  );
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || value === null || typeof value === 'string';
}

function badResponse(detail: string): VaultDescriptorError {
  return new VaultDescriptorError(
    'VAULT_DESCRIPTOR_BAD_RESPONSE',
    `Invalid vault descriptor: ${detail}`
  );
}

function assertMember(member: unknown, index: number): asserts member is VaultDescriptorMember {
  if (!isRecord(member)) throw badResponse(`member ${String(index)} is not an object`);
  if (!isNonEmptyString(member.wkIdentity)) {
    throw badResponse(`member ${String(index)} has no wkIdentity`);
  }
  if (
    typeof member.walletPubKey !== 'string' ||
    !COMPRESSED_PUBKEY_RE.test(member.walletPubKey) ||
    typeof member.keyPubKey !== 'string' ||
    !COMPRESSED_PUBKEY_RE.test(member.keyPubKey)
  ) {
    throw badResponse(`member ${String(index)} has invalid identity public keys`);
  }
  for (const field of [
    'xpubWallet',
    'xpubKey',
    'walletXpubSignature',
    'keyXpubSignature',
  ] as const) {
    if (!isOptionalString(member[field])) {
      throw badResponse(`member ${String(index)} has an invalid ${field}`);
    }
  }
}

/**
 * Validate the relay's `{ descriptor, descriptorHash }` payload for the vault
 * the app asked about. Returns the SAME objects it was given (never a copy):
 * the devices hash the descriptor themselves, so the app must not normalise,
 * reorder or re-type anything.
 *
 * @throws VaultDescriptorError('VAULT_DESCRIPTOR_BAD_RESPONSE') when malformed
 * @throws VaultDescriptorError('VAULT_DESCRIPTOR_REQUEST_MISMATCH') when it
 *         describes another org/vault than the one requested
 */
export function parseVaultDescriptorBundle(
  data: unknown,
  expected: { orgId: string; vaultId: string }
): VaultDescriptorBundle {
  if (!isRecord(data)) throw badResponse('empty response');
  const { descriptor, descriptorHash } = data;
  if (typeof descriptorHash !== 'string' || !HASH_RE.test(descriptorHash)) {
    throw badResponse('descriptorHash must be 64 lowercase hex characters');
  }
  if (!isRecord(descriptor)) throw badResponse('descriptor is missing');
  if (descriptor.v !== VAULT_DESCRIPTOR_VERSION) {
    throw badResponse(`unsupported version ${String(descriptor.v)}`);
  }
  if (!isNonEmptyString(descriptor.orgId) || !isNonEmptyString(descriptor.vaultId)) {
    throw badResponse('orgId and vaultId are required');
  }
  if (descriptor.orgId !== expected.orgId || descriptor.vaultId !== expected.vaultId) {
    throw new VaultDescriptorError(
      'VAULT_DESCRIPTOR_REQUEST_MISMATCH',
      'The vault descriptor belongs to a different vault'
    );
  }
  if (!isIntegerInRange(descriptor.orgIndex, ORG_INDEX_MIN, ORG_INDEX_MAX + 1)) {
    throw badResponse('orgIndex must be an integer in 100-99999');
  }
  if (!isIntegerInRange(descriptor.vaultIndex, 0, VAULT_INDEX_LIMIT)) {
    throw badResponse('vaultIndex must be an integer in [0, 2^31)');
  }
  if (!isNonEmptyString(descriptor.chain)) throw badResponse('chain is required');
  if (
    descriptor.signingMode !== undefined &&
    descriptor.signingMode !== null &&
    !isNonEmptyString(descriptor.signingMode)
  ) {
    throw badResponse('signingMode must be a string');
  }
  const members = descriptor.members;
  if (!Array.isArray(members) || members.length === 0) {
    throw badResponse('members must be a non-empty array');
  }
  members.forEach((member, index) => {
    assertMember(member, index);
  });
  const typedMembers = members as VaultDescriptorMember[];
  let previous: string | null = null;
  for (const member of typedMembers) {
    // Plain string compare, strictly ascending: sorted and unique (VD-1).
    if (previous !== null && !(previous < member.wkIdentity)) {
      throw badResponse('members must be sorted and unique by wkIdentity');
    }
    previous = member.wkIdentity;
  }
  if (!isIntegerInRange(descriptor.requiredSigners, 1, typedMembers.length + 1)) {
    throw badResponse('requiredSigners must be an integer in 1..N');
  }
  return { descriptor: descriptor as unknown as VaultDescriptor, descriptorHash };
}

/**
 * The descriptor must describe the vault the request is about: same org index,
 * vault index and chain as the params the app is about to send. A mismatch
 * means stale or inconsistent data; the devices would refuse anyway, so the
 * app refuses first with a clearer message.
 */
export function assertDescriptorMatchesRequest(
  descriptor: VaultDescriptor,
  request: { orgIndex: number; vaultIndex: number; chain: string }
): void {
  if (
    descriptor.orgIndex !== request.orgIndex ||
    descriptor.vaultIndex !== request.vaultIndex ||
    descriptor.chain !== request.chain
  ) {
    throw new VaultDescriptorError(
      'VAULT_DESCRIPTOR_REQUEST_MISMATCH',
      'The vault descriptor does not match this request'
    );
  }
}

/**
 * HTTP status of a failed request: the apiClient interceptor re-throws error
 * envelopes (`{ status:'error', data:{ message } }`) as `Error & { status }`;
 * a raw AxiosError carries `response.status`.
 */
function httpStatusOf(err: unknown): number | undefined {
  if (!isRecord(err)) return undefined;
  if (typeof err.status === 'number') return err.status;
  const response = err.response;
  if (!isRecord(response)) return undefined;
  return typeof response.status === 'number' ? response.status : undefined;
}

/** True for a "vault not fully set up" answer from the descriptor endpoint. */
export function isDescriptorNotReady(status: number | undefined, errorCode?: unknown): boolean {
  return status === 409 || (typeof errorCode === 'string' && NOT_READY_CODES.has(errorCode));
}

/** Normalise any failure while fetching the descriptor to a VaultDescriptorError. */
export function toVaultDescriptorError(err: unknown): VaultDescriptorError {
  if (err instanceof VaultDescriptorError) return err;
  const errorCode = isRecord(err) ? err.errorCode : undefined;
  return vaultDescriptorErrorFor(descriptorRefusalCode(httpStatusOf(err), errorCode));
}

const DESCRIPTOR_ERROR_TEXT: Record<VaultDescriptorErrorCode, string> = {
  VAULT_DESCRIPTOR_UNAVAILABLE: 'The vault descriptor could not be loaded',
  VAULT_DESCRIPTOR_NOT_READY: 'The vault is not fully set up',
  VAULT_DESCRIPTOR_BAD_RESPONSE: 'The server could not provide a valid vault descriptor',
  VAULT_DESCRIPTOR_REQUEST_MISMATCH: 'The vault descriptor does not match this request',
};

export function vaultDescriptorErrorFor(code: VaultDescriptorErrorCode): VaultDescriptorError {
  return new VaultDescriptorError(code, DESCRIPTOR_ERROR_TEXT[code]);
}

export type VaultDescriptorFetcher = (
  orgId: string,
  vaultId: string
) => Promise<VaultDescriptorBundle>;

/**
 * Fetch the descriptor for a device request and check it belongs to that
 * request. Call it right before window.ssp.request; on any throw, do NOT call
 * the wallet.
 */
export async function loadVaultDescriptorForRequest(
  fetchDescriptor: VaultDescriptorFetcher,
  target: { orgId?: string | null; vaultId?: string | null },
  request: { orgIndex: number; vaultIndex: number; chain: string }
): Promise<VaultDescriptorBundle> {
  if (!target.orgId || !target.vaultId) {
    throw new VaultDescriptorError(
      'VAULT_DESCRIPTOR_UNAVAILABLE',
      'The vault for this request is unknown'
    );
  }
  let bundle: VaultDescriptorBundle;
  try {
    bundle = await fetchDescriptor(target.orgId, target.vaultId);
  } catch (err) {
    throw toVaultDescriptorError(err);
  }
  assertDescriptorMatchesRequest(bundle.descriptor, request);
  return bundle;
}

/**
 * loadVaultDescriptorForRequest for a wallet that verifies descriptors; null
 * (send today's params) for one that doesn't advertise `vault_descriptor_v1`.
 * Throws like loadVaultDescriptorForRequest, and then the wallet must not be
 * called.
 */
export async function loadVaultDescriptorIfSupported(
  fetchDescriptor: VaultDescriptorFetcher,
  target: { orgId?: string | null; vaultId?: string | null },
  request: { orgIndex: number; vaultIndex: number; chain: string },
  provider?: unknown
): Promise<VaultDescriptorBundle | null> {
  // An undefined provider falls back to window.ssp (default parameter).
  if (!walletHasCapability(WALLET_CAPABILITY.vaultDescriptor, provider)) return null;
  return loadVaultDescriptorForRequest(fetchDescriptor, target, request);
}

/**
 * Fetch the descriptor of the vault an address belongs to (VD-5). The address
 * must be on the descriptor's chain; the wallet re-derives the rest.
 */
export async function loadVaultDescriptorForAddress(
  fetchDescriptor: VaultDescriptorFetcher,
  orgId: string | null | undefined,
  address: { vaultId: string; chain: string }
): Promise<VaultDescriptorBundle> {
  if (!orgId || !address.vaultId) {
    throw new VaultDescriptorError(
      'VAULT_DESCRIPTOR_UNAVAILABLE',
      'The vault for this address is unknown'
    );
  }
  let bundle: VaultDescriptorBundle;
  try {
    bundle = await fetchDescriptor(orgId, address.vaultId);
  } catch (err) {
    throw toVaultDescriptorError(err);
  }
  if (bundle.descriptor.chain !== address.chain) {
    throw new VaultDescriptorError(
      'VAULT_DESCRIPTOR_REQUEST_MISMATCH',
      'The vault descriptor is for another chain'
    );
  }
  return bundle;
}

/**
 * Add the descriptor + hash to window.ssp params, passing both through
 * unchanged. Without a bundle (a wallet without descriptor support) the params
 * are returned as they are.
 */
export function withVaultDescriptor<T extends Record<string, unknown>>(
  params: T,
  bundle: VaultDescriptorBundle
): T & VaultDescriptorParams;
export function withVaultDescriptor<T extends Record<string, unknown>>(
  params: T,
  bundle: VaultDescriptorBundle | null
): T | (T & VaultDescriptorParams);
export function withVaultDescriptor<T extends Record<string, unknown>>(
  params: T,
  bundle: VaultDescriptorBundle | null
): T | (T & VaultDescriptorParams) {
  if (!bundle) return params;
  return {
    ...params,
    vaultDescriptor: bundle.descriptor,
    vaultDescriptorHash: bundle.descriptorHash,
  };
}

// ---------------------------------------------------------------------------
// VD-5: show a vault address on SSP Wallet
// ---------------------------------------------------------------------------

export const VAULT_ADDRESS_METHOD = 'enterprise_vault_address';

export interface VaultAddressRequestParams extends VaultDescriptorParams {
  typeIndex: number;
  addressIndex: number;
  expectedAddress: string;
  vaultName: string;
  orgName: string;
  origin: string;
  siteName: string;
  iconUrl: string;
}

/**
 * Params for window.ssp.request('enterprise_vault_address').
 *
 * Every enterprise vault address lives at
 * m/48'/coin'/orgIndex'/script'/vaultIndex/addressIndex (relay
 * vaultAddressService derives `xpub/vaultIndex/addressIndex`), so the path's
 * type level (`typeIndex`) is the vault index for every vault address.
 */
export function buildVaultAddressRequest(
  bundle: VaultDescriptorBundle,
  address: { addressIndex: number; address: string },
  context: { vaultName?: string; orgName?: string; origin: string }
): VaultAddressRequestParams {
  return {
    typeIndex: bundle.descriptor.vaultIndex,
    addressIndex: address.addressIndex,
    expectedAddress: address.address,
    vaultName: context.vaultName ?? '',
    orgName: context.orgName ?? '',
    origin: context.origin,
    siteName: 'SSP Enterprise',
    iconUrl: 'https://enterprise.sspwallet.com/ssp-logo-black-256x256.png',
    vaultDescriptor: bundle.descriptor,
    vaultDescriptorHash: bundle.descriptorHash,
  };
}

const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;

/** Exact compare; EVM hex addresses compare case-insensitively (checksum casing). */
export function sameVaultAddress(a: string, b: string): boolean {
  if (EVM_ADDRESS_RE.test(a) && EVM_ADDRESS_RE.test(b)) {
    return a.toLowerCase() === b.toLowerCase();
  }
  return a === b;
}

export type VaultAddressCheck =
  | { status: 'match'; address: string }
  | { status: 'mismatch'; address: string | null };

/**
 * Interpret the wallet's `{ address, matches }` answer. MATCH needs BOTH the
 * wallet's verdict and the app's own compare of the derived address with the
 * address on screen; anything else is a mismatch (fail closed).
 */
export function interpretVaultAddressResult(
  result: unknown,
  expectedAddress: string
): VaultAddressCheck {
  if (!isRecord(result) || typeof result.address !== 'string' || result.address.length === 0) {
    return { status: 'mismatch', address: null };
  }
  const derived = result.address;
  if (result.matches === true && sameVaultAddress(derived, expectedAddress)) {
    return { status: 'match', address: derived };
  }
  return { status: 'mismatch', address: derived };
}

/** The fields of a verification state that tie it to one address. */
export interface VaultAddressVerdictLike {
  status: string;
  /** The address the wallet derived (MATCH / MISMATCH). */
  address?: string | null;
  /** The on-screen address the check was requested for. */
  target?: string;
}

/**
 * Bind a verification state to the address CURRENTLY on screen (review D-5).
 * A state that was produced for another address (the address changed after
 * the check, or a late answer for a previous address) is stale and renders as
 * no verdict at all. MATCH additionally requires the derived address to equal
 * the current address (EVM case-insensitive): otherwise it is never shown.
 */
export function bindVerdictToAddress<S extends VaultAddressVerdictLike>(
  state: S,
  currentAddress: string
): S | { status: 'idle' } {
  if (state.target !== undefined && !sameVaultAddress(state.target, currentAddress)) {
    return { status: 'idle' };
  }
  if (state.status === 'match') {
    const isCurrent =
      typeof state.target === 'string' &&
      typeof state.address === 'string' &&
      sameVaultAddress(state.address, currentAddress);
    if (!isCurrent) return { status: 'idle' };
  }
  return state;
}

// ---------------------------------------------------------------------------
// Device refusals (VD-3 rules 6 and 7) and old wallets
// ---------------------------------------------------------------------------

export type VaultDeviceRefusal =
  /** The descriptor differs from the one pinned for this vault (hard refusal). */
  | 'descriptor_changed'
  /** The device received no descriptor (old app, or an old wallet in between). */
  | 'descriptor_missing'
  /** The user declined to pin the vault on first sight. */
  | 'pin_refused'
  /** The descriptor failed the device's verification (hash, keys, attestations). */
  | 'descriptor_invalid';

const REFUSAL_BY_CODE: Record<string, VaultDeviceRefusal> = {
  VAULT_DESCRIPTOR_CHANGED: 'descriptor_changed',
  VAULT_CONFIGURATION_CHANGED: 'descriptor_changed',
  VAULT_PIN_MISMATCH: 'descriptor_changed',
  VAULT_DESCRIPTOR_MISSING: 'descriptor_missing',
  VAULT_PIN_REFUSED: 'pin_refused',
  VAULT_PIN_REJECTED: 'pin_refused',
  VAULT_PIN_DECLINED: 'pin_refused',
  VAULT_DESCRIPTOR_INVALID: 'descriptor_invalid',
  VAULT_DESCRIPTOR_REJECTED: 'descriptor_invalid',
};

// Message fallbacks, most severe first, for devices that send only text.
const REFUSAL_PATTERNS: Array<{ test: RegExp; refusal: VaultDeviceRefusal }> = [
  {
    test: /configuration (has )?changed|pin mismatch|does(n't| not) match the pinned|differs from the pinned/i,
    refusal: 'descriptor_changed',
  },
  { test: /descriptor (is )?missing|missing (vault )?descriptor/i, refusal: 'descriptor_missing' },
  {
    test: /\bpin(ning)?\b[^.]*\b(refused|rejected|declined|cancell?ed)\b|\b(refused|rejected|declined|cancell?ed)\b[^.]*\bpin\b/i,
    refusal: 'pin_refused',
  },
  {
    test: /descriptor[^.]*\b(invalid|failed|could not be verified|not verified)\b|invalid (vault )?descriptor/i,
    refusal: 'descriptor_invalid',
  },
];

/** Classify a wallet/Key error as a VD refusal, or null when it is something else. */
export function classifyVaultDeviceRefusal(
  message: string | null | undefined,
  errorCode?: string | null
): VaultDeviceRefusal | null {
  if (errorCode && REFUSAL_BY_CODE[errorCode]) return REFUSAL_BY_CODE[errorCode];
  if (!message) return null;
  for (const { test, refusal } of REFUSAL_PATTERNS) {
    if (test.test(message)) return refusal;
  }
  return null;
}

/** App error code reported for each refusal (mapped to copy by friendlyError). */
export const VAULT_DEVICE_REFUSAL_CODES: Record<VaultDeviceRefusal, string> = {
  descriptor_changed: 'VAULT_DESCRIPTOR_CHANGED',
  descriptor_missing: 'VAULT_DESCRIPTOR_MISSING',
  pin_refused: 'VAULT_PIN_REFUSED',
  descriptor_invalid: 'VAULT_DESCRIPTOR_INVALID',
};

/** The app error code for a VD refusal in a wallet/Key error, or null. */
export function vaultDeviceRefusalCode(
  message: string | null | undefined,
  errorCode?: string | null
): string | null {
  const refusal = classifyVaultDeviceRefusal(message, errorCode);
  return refusal ? VAULT_DEVICE_REFUSAL_CODES[refusal] : null;
}

/**
 * True when the wallet doesn't know the method: a wallet without VD-5 answers
 * 'Unsupported method' (code 4200); released wallets answer 'Invalid method'.
 */
export function isUnsupportedWalletMethod(
  message: string | null | undefined,
  code?: unknown,
  errorCode?: string | null
): boolean {
  if (code === 4200) return true;
  if (errorCode === 'UNSUPPORTED_METHOD' || errorCode === 'METHOD_NOT_SUPPORTED') return true;
  return (
    !!message &&
    /unsupported method|invalid method|unknown method|method not (supported|found)/i.test(message)
  );
}

/** Pull message / code / errorCode out of a wallet error (thrown or returned). */
export function readWalletError(source: unknown): {
  message: string;
  code?: unknown;
  errorCode: string | null;
} {
  if (typeof source === 'string') return { message: source, errorCode: null };
  if (!isRecord(source)) return { message: '', errorCode: null };
  const r = source;
  const text = [r.message, r.data, r.result, r.error].find(
    (v): v is string => typeof v === 'string' && v.length > 0
  );
  return {
    message: text ?? '',
    code: r.code,
    errorCode: typeof r.errorCode === 'string' ? r.errorCode : null,
  };
}
